import React from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { PageHeader } from '@/components/PageHeader';
import { EmptyState } from '@/components/EmptyState';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Separator } from '@/components/ui/separator';
import { Badge } from '@/components/ui/badge';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader,
  AlertDialogTitle, AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import { BrainCircuit, Plus, Send, Loader2, Wrench, User, Trash2, Archive, Activity, Zap, MessageSquare } from 'lucide-react';
import { toast } from 'sonner';
import { relativeTime, shortId } from '@/lib/utils';

export default function Master() {
  const { sessionId } = useParams();
  const nav = useNavigate();
  const qc = useQueryClient();

  const { data: sessions, isLoading: sessionsLoading } = useQuery({
    queryKey: ['master-sessions'],
    queryFn: () => api.get('/master/sessions').then(r => r.data),
    refetchInterval: 20_000,
  });

  const createMut = useMutation({
    mutationFn: () => api.post('/master/sessions', { title: 'Untitled campaign' }).then(r => r.data),
    onSuccess: (s) => {
      qc.invalidateQueries({ queryKey: ['master-sessions'] });
      nav(`/master/${s.id}`);
    },
    onError: (e) => toast.error(e?.response?.data?.detail || 'Could not create session'),
  });

  return (
    <div className="flex h-[calc(100vh-56px)] flex-col gap-4 overflow-hidden" data-testid="master-page">
      <PageHeader
        eyebrow="Orchestration"
        title="Master Agent"
        description="Chat with your CEO Agent to plan and execute parasite-SEO campaigns. It can list operatives, warm them up, submit tasks, and research ICPs on your behalf."
        actions={
          <Button onClick={() => createMut.mutate()} disabled={createMut.isPending} data-testid="master-new-session-button">
            {createMut.isPending ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Plus className="mr-2 h-4 w-4" />}
            New session
          </Button>
        }
      />

      <div className="grid flex-1 gap-4 lg:grid-cols-[280px_1fr] min-h-0">
        {/* Sessions sidebar */}
        <div className="rounded-lg border border-white/5 bg-[hsl(var(--surface-1))] flex flex-col min-h-0" data-testid="master-sessions-sidebar">
          <div className="border-b border-white/5 px-3 py-2.5 text-[10px] uppercase tracking-[0.14em] text-[hsl(var(--text-3))]">
            Sessions
          </div>
          <ScrollArea className="flex-1 min-h-0">
            {sessionsLoading ? (
              <div className="p-4 text-xs text-[hsl(var(--text-3))]">Loading…</div>
            ) : !sessions?.items?.length ? (
              <div className="p-4 text-xs text-[hsl(var(--text-3))]">No sessions yet. Click <span className="font-mono">New session</span> to start a campaign.</div>
            ) : (
              <div className="py-1">
                {sessions.items.map((s) => (
                  <button
                    key={s.id}
                    onClick={() => nav(`/master/${s.id}`)}
                    className={`w-full px-3 py-2 text-left hover:bg-[hsl(var(--surface-2))] transition-colors border-l-2 ${sessionId === s.id ? 'border-[hsl(var(--primary))] bg-[hsl(var(--surface-2))]' : 'border-transparent'}`}
                    data-testid={`master-session-row-${s.id}`}
                  >
                    <div className="text-sm font-medium truncate">{s.title}</div>
                    <div className="mt-0.5 text-[11px] text-[hsl(var(--text-3))] truncate font-mono">
                      {s.last_message_preview || <span className="opacity-50">(no messages yet)</span>}
                    </div>
                    <div className="mt-1 flex items-center gap-2 text-[10px] text-[hsl(var(--text-3))]">
                      <span className="tabular">{s.total_tokens || 0} tok</span>
                      <span>·</span>
                      <span className="tabular">{s.tool_calls_count || 0} tools</span>
                      <span>·</span>
                      <span>{relativeTime(s.updated_at)}</span>
                    </div>
                  </button>
                ))}
              </div>
            )}
          </ScrollArea>
        </div>

        {/* Chat pane */}
        <div className="rounded-lg border border-white/5 bg-[hsl(var(--surface-1))] flex flex-col min-h-0">
          {sessionId ? (
            <ChatPane sessionId={sessionId} />
          ) : (
            <EmptyState
              icon={BrainCircuit}
              title="Pick a session — or start a new one"
              description="Ask me to research an ICP, warm up a cold agent, or run a multi-step promote campaign. I'll chain tools to get it done."
              action={<Button onClick={() => createMut.mutate()}>Start new campaign</Button>}
            />
          )}
        </div>
      </div>
    </div>
  );
}


function ChatPane({ sessionId }) {
  const qc = useQueryClient();
  const nav = useNavigate();
  const scrollRef = React.useRef(null);
  const [input, setInput] = React.useState('');

  const { data, isLoading } = useQuery({
    queryKey: ['master-session', sessionId],
    queryFn: () => api.get(`/master/sessions/${sessionId}`).then(r => r.data),
    refetchInterval: 30_000,
  });

  const sendMut = useMutation({
    mutationFn: (content) => api.post(`/master/sessions/${sessionId}/messages`, { content }, { timeout: 180_000 }).then(r => r.data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['master-session', sessionId] });
      qc.invalidateQueries({ queryKey: ['master-sessions'] });
      setInput('');
      setTimeout(() => scrollRef.current?.scrollTo({ top: 1e9, behavior: 'smooth' }), 150);
    },
    onError: (e) => toast.error(e?.response?.data?.detail || 'Send failed', { description: e?.message }),
  });

  const delMut = useMutation({
    mutationFn: () => api.delete(`/master/sessions/${sessionId}`),
    onSuccess: () => {
      toast.success('Session deleted');
      qc.invalidateQueries({ queryKey: ['master-sessions'] });
      nav('/master');
    },
  });

  React.useEffect(() => {
    if (data?.messages?.length) {
      setTimeout(() => scrollRef.current?.scrollTo({ top: 1e9 }), 100);
    }
  }, [data?.messages?.length]);

  const submit = () => {
    const v = input.trim();
    if (!v || sendMut.isPending) return;
    sendMut.mutate(v);
  };

  if (isLoading) {
    return <div className="flex-1 flex items-center justify-center text-sm text-[hsl(var(--text-3))]" data-testid="chat-loading">Loading session…</div>;
  }
  if (!data) return null;

  const { session, messages, memory } = data;

  return (
    <div className="flex flex-col min-h-0 flex-1" data-testid="chat-pane">
      {/* Session header */}
      <div className="flex items-center justify-between border-b border-white/5 px-4 py-2.5">
        <div className="min-w-0 flex-1">
          <div className="text-sm font-medium truncate">{session.title}</div>
          <div className="mt-0.5 flex items-center gap-3 text-[11px] text-[hsl(var(--text-3))] font-mono">
            <span>{session.model.split('/').pop()}</span>
            <span>·</span>
            <span className="tabular">{session.total_tokens} tokens · ${session.total_cost_usd.toFixed(4)}</span>
            <span>·</span>
            <span className="tabular">{session.tool_calls_count} tool calls</span>
          </div>
        </div>
        <div className="flex items-center gap-1 shrink-0">
          {Object.keys(memory || {}).length > 0 && (
            <Badge variant="outline" className="text-[10px] font-mono" data-testid="memory-pill">
              <Zap className="h-2.5 w-2.5 mr-1" />
              {Object.keys(memory).length} memories
            </Badge>
          )}
          <AlertDialog>
            <AlertDialogTrigger asChild>
              <button className="rounded p-1.5 text-[hsl(var(--text-3))] hover:bg-[hsl(var(--surface-2))] hover:text-[hsl(var(--danger))] transition-colors" aria-label="Delete session" data-testid="delete-session-button">
                <Trash2 className="h-4 w-4" />
              </button>
            </AlertDialogTrigger>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>Delete this session?</AlertDialogTitle>
                <AlertDialogDescription>All messages and memory will be removed. This cannot be undone.</AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel>Cancel</AlertDialogCancel>
                <AlertDialogAction onClick={() => delMut.mutate()}>Delete</AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </div>
      </div>

      {/* Memory panel (collapsible) */}
      {Object.keys(memory || {}).length > 0 && (
        <details className="border-b border-white/5 px-4 py-2 group">
          <summary className="cursor-pointer text-[11px] uppercase tracking-wider text-[hsl(var(--text-3))] flex items-center gap-1">
            <Zap className="h-3 w-3" /> Session memory
            <span className="text-[hsl(var(--text-3))] opacity-50 ml-auto group-open:hidden">click to expand</span>
          </summary>
          <pre className="mt-2 text-[11px] text-[hsl(var(--text-2))] font-mono overflow-x-auto whitespace-pre-wrap bg-[hsl(var(--surface-2))] p-2 rounded" data-testid="memory-panel">
            {JSON.stringify(memory, null, 2)}
          </pre>
        </details>
      )}

      {/* Messages */}
      <div ref={scrollRef} className="flex-1 overflow-y-auto px-4 py-3 space-y-3 min-h-0" data-testid="master-messages">
        {messages.length === 0 ? (
          <div className="h-full flex flex-col items-center justify-center gap-3 text-center px-8 py-12">
            <BrainCircuit className="h-10 w-10 text-[hsl(var(--primary))] opacity-60" />
            <div className="text-sm font-medium">Your CEO Agent is ready</div>
            <div className="text-xs text-[hsl(var(--text-3))] max-w-sm">
              Try <button onClick={() => setInput('List my operatives and their health scores')} className="underline hover:text-[hsl(var(--text-1))]">listing operatives</button>,
              or <button onClick={() => setInput('Research ICP and pain points for a headless CMS product aimed at indie devs. Remember the results for this campaign.')} className="underline hover:text-[hsl(var(--text-1))]">researching an ICP</button>.
            </div>
          </div>
        ) : messages.map((m) => <MessageBubble key={m.id} message={m} />)}
        {sendMut.isPending && (
          <div className="flex items-center gap-2 text-xs text-[hsl(var(--text-3))] pl-10" data-testid="thinking-indicator">
            <Loader2 className="h-3 w-3 animate-spin" />
            <span>CEO Agent is thinking…</span>
          </div>
        )}
      </div>

      {/* Composer */}
      <div className="border-t border-white/5 p-3">
        <div className="flex items-end gap-2">
          <Textarea
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) { e.preventDefault(); submit(); }
            }}
            placeholder="Message the CEO Agent… (⌘/Ctrl+Enter to send)"
            rows={2}
            className="resize-none min-h-[56px] max-h-[200px]"
            disabled={sendMut.isPending}
            data-testid="master-message-input"
          />
          <Button
            onClick={submit}
            disabled={!input.trim() || sendMut.isPending}
            size="sm"
            className="h-[56px] w-12 shrink-0"
            data-testid="master-send-button"
          >
            {sendMut.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
          </Button>
        </div>
      </div>
    </div>
  );
}


function MessageBubble({ message: m }) {
  if (m.role === 'user') {
    return (
      <div className="flex gap-3 flex-row-reverse" data-testid={`msg-user-${m.id}`}>
        <div className="shrink-0 h-7 w-7 rounded-full bg-[hsl(var(--surface-3))] flex items-center justify-center text-[10px]">
          <User className="h-3.5 w-3.5" />
        </div>
        <div className="max-w-[75%] rounded-xl rounded-tr-sm bg-[hsl(var(--primary))] px-3.5 py-2 text-sm text-[hsl(var(--primary-foreground,0_0%_100%))]">
          {m.content}
        </div>
      </div>
    );
  }
  if (m.role === 'tool') {
    const isError = m.tool_result && 'error' in m.tool_result;
    return (
      <details className="pl-10" data-testid={`msg-tool-${m.id}`}>
        <summary className={`inline-flex items-center gap-1.5 cursor-pointer text-[11px] font-mono rounded-md border px-2 py-1 transition-colors ${isError ? 'border-[hsl(var(--danger))]/40 bg-[hsl(var(--danger))]/10 text-[hsl(var(--danger))]' : 'border-white/10 bg-[hsl(var(--surface-2))] text-[hsl(var(--text-3))] hover:bg-[hsl(var(--surface-3))]'}`}>
          <Activity className="h-3 w-3" />
          <span>tool_result</span>
          {isError && <span>· error</span>}
        </summary>
        <pre className="mt-1 text-[10px] font-mono text-[hsl(var(--text-3))] bg-[hsl(var(--surface-2))] p-2 rounded overflow-x-auto whitespace-pre-wrap max-h-64">
          {JSON.stringify(m.tool_result, null, 2)}
        </pre>
      </details>
    );
  }
  // assistant
  return (
    <div className="flex gap-3" data-testid={`msg-assistant-${m.id}`}>
      <div className="shrink-0 h-7 w-7 rounded-full bg-[hsl(var(--primary))]/15 flex items-center justify-center">
        <BrainCircuit className="h-3.5 w-3.5 text-[hsl(var(--primary))]" />
      </div>
      <div className="max-w-[85%] flex-1 space-y-1.5 min-w-0">
        {m.tool_calls && m.tool_calls.length > 0 && (
          <div className="flex flex-wrap gap-1">
            {m.tool_calls.map((tc) => (
              <span key={tc.id} className="inline-flex items-center gap-1 rounded-md border border-[hsl(var(--primary))]/30 bg-[hsl(var(--primary))]/10 px-2 py-0.5 text-[10px] font-mono text-[hsl(var(--primary))]">
                <Wrench className="h-2.5 w-2.5" />
                {tc.function?.name}
                {tc.function?.arguments && tc.function.arguments !== '{}' && (
                  <span className="opacity-60">({truncateArgs(tc.function.arguments)})</span>
                )}
              </span>
            ))}
          </div>
        )}
        {m.content && (
          <div className="text-sm text-[hsl(var(--text-1))] whitespace-pre-wrap leading-relaxed">
            {m.content}
          </div>
        )}
      </div>
    </div>
  );
}


function truncateArgs(rawArgs) {
  try {
    const obj = typeof rawArgs === 'string' ? JSON.parse(rawArgs) : rawArgs;
    const entries = Object.entries(obj).map(([k, v]) => `${k}=${String(v).slice(0, 20)}`);
    return entries.join(', ').slice(0, 60);
  } catch {
    return String(rawArgs).slice(0, 40);
  }
}
